import { useEffect, useMemo, useState } from "react";
import { getLiftDashboard } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { TrendingUp, TrendingDown, Circle, FileCheck2, Send, ExternalLink } from "lucide-react";

/**
 * Score-Lift Dashboard.
 *
 * Surfaces the longitudinal impact of the FieldGuide subscription for
 * properties with ≥2 completed grades. Data source is the R3-backed
 * per-analysis lift-dashboard endpoint (email-unlocked; no partner key).
 *
 * The endpoint returns `show: false` when there aren't enough runs yet —
 * we render nothing in that case so first-run hotels don't see an empty
 * chart with "0" everywhere.
 */
export default function LiftDashboard({ analysisId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await getLiftDashboard(analysisId);
        if (alive) setData(d);
      } catch {
        if (alive) setData({ show: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [analysisId]);

  if (loading || !data?.show) return null;

  const totalLift = data.total_score_lift ?? 0;
  const liftPositive = totalLift >= 0;
  const stats = data.topic_stats || {};

  return (
    <section
      className="mt-16 fade-in"
      data-testid={TESTIDS.liftDashboardSection}
    >
      <div className="mb-4">
        <div className="label-eyebrow mb-1">Your Local Authority · over time</div>
        <div className="font-serif text-2xl">Since your first grade</div>
      </div>

      {/* Top row: total lift + KPI counts */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="glass rounded-2xl p-5" data-testid={TESTIDS.liftDashboardTotalLift}>
          <div className="label-eyebrow mb-2">Score change</div>
          <div className="flex items-baseline gap-2">
            <div
              className={`font-serif text-5xl leading-none ${
                liftPositive ? "score-strong" : "score-low"
              }`}
            >
              {liftPositive ? "+" : ""}{totalLift}
            </div>
            {liftPositive ? (
              <TrendingUp className="w-5 h-5" style={{ color: "var(--brand-sage-deep)" }} />
            ) : (
              <TrendingDown className="w-5 h-5" style={{ color: "var(--score-low)" }} />
            )}
          </div>
          <div className="font-mono text-xs mt-1 text-[color:var(--text-muted)]">
            {data.first_score} → {data.latest_score}
          </div>
        </div>

        <StatCard
          label="Guides published"
          value={stats.published || 0}
          icon={<FileCheck2 className="w-4 h-4" />}
          testid={TESTIDS.liftDashboardPublishedCount}
          tone="strong"
        />
        <StatCard
          label="In flight"
          value={stats.assigned || 0}
          icon={<Send className="w-4 h-4" />}
          testid={TESTIDS.liftDashboardAssignedCount}
        />
        <StatCard
          label="Open opportunities"
          value={stats.open || 0}
          icon={<Circle className="w-4 h-4" />}
          testid={TESTIDS.liftDashboardOpenCount}
          tone="muted"
        />
      </div>

      {/* Trendline */}
      <TrendChart runs={data.runs} />

      {/* Last published */}
      {data.last_published?.title && (
        <div
          className="mt-6 rounded-2xl p-5 flex items-start gap-3"
          style={{
            background: "rgba(107,122,85,0.08)",
            border: "1px solid rgba(107,122,85,0.25)",
          }}
          data-testid={TESTIDS.liftDashboardLastPublished}
        >
          <FileCheck2
            className="w-5 h-5 mt-1 shrink-0"
            style={{ color: "var(--brand-sage-deep)" }}
          />
          <div className="flex-1">
            <div className="label-eyebrow mb-1">Most recently published</div>
            <div className="font-serif text-lg leading-snug">
              {data.last_published.title}
            </div>
            {data.last_published.published_url && (
              <a
                href={data.last_published.published_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm mt-1"
                style={{ color: "var(--brand-sage-deep)" }}
              >
                View live guide <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


function StatCard({ label, value, icon, testid, tone }) {
  const toneClass =
    tone === "strong" ? "score-strong" : tone === "muted" ? "text-[color:var(--text-muted)]" : "";
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="label-eyebrow mb-2 flex items-center gap-2">
        <span style={{ color: "var(--brand-sage-deep)" }}>{icon}</span>
        {label}
      </div>
      <div className={`font-serif text-5xl leading-none ${toneClass}`}>{value}</div>
    </div>
  );
}


/**
 * Simple inline SVG sparkline of headline_score across runs.
 * No chart library needed — this is 4-12 points max, so a hand-rolled
 * polyline keeps bundle size flat and lets us style dots inline.
 */
function TrendChart({ runs }) {
  const chart = useMemo(() => {
    if (!runs || runs.length < 2) return null;
    const W = 640;
    const H = 160;
    const PAD_X = 32;
    const PAD_Y = 24;
    const scores = runs.map((r) => r.headline_score);
    const min = Math.min(...scores);
    const max = Math.max(...scores);
    const range = Math.max(1, max - min);
    const stepX = (W - PAD_X * 2) / (runs.length - 1);
    const points = runs.map((r, i) => {
      const x = PAD_X + i * stepX;
      const y = PAD_Y + (H - PAD_Y * 2) * (1 - (r.headline_score - min) / range);
      return { x, y, r };
    });
    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
    return { W, H, points, path, min, max };
  }, [runs]);

  if (!chart) return null;

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid={TESTIDS.liftDashboardTrendline}>
      <div className="label-eyebrow mb-3">Score history</div>
      <svg viewBox={`0 0 ${chart.W} ${chart.H}`} className="w-full h-auto">
        {/* Baseline area */}
        <path
          d={`${chart.path} L${chart.points[chart.points.length - 1].x},${chart.H - 24} L${chart.points[0].x},${chart.H - 24} Z`}
          fill="rgba(107,122,85,0.12)"
        />
        {/* Trendline */}
        <path
          d={chart.path}
          fill="none"
          stroke="var(--brand-sage-deep, #4d5a3d)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Dots */}
        {chart.points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r={p.r.is_current ? 6 : 4}
              fill={p.r.is_current ? "var(--brand-copper, #a67c3f)" : "var(--brand-sage-deep, #4d5a3d)"}
              stroke="white"
              strokeWidth="2"
            />
            <text
              x={p.x}
              y={p.y - 12}
              textAnchor="middle"
              fontSize="12"
              fill="var(--text-primary, #333)"
              fontWeight={p.r.is_current ? 700 : 500}
            >
              {p.r.headline_score}
            </text>
            <text
              x={p.x}
              y={chart.H - 6}
              textAnchor="middle"
              fontSize="10"
              fill="var(--text-muted, #888)"
            >
              {fmtDate(p.r.completed_at)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
